import { skipToken, useSuspenseQuery } from "@apollo/client";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  useTheme,
} from "@mui/material";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { PREVIEW_ICON_GAP } from "components/PreviewFeature/constants";
import { StaffInDevelopmentIcon } from "components/PreviewFeature/StaffInDevelopmentIcon";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { gql } from "generated-graphql";
import { useMemo, useState } from "react";

import { SelectDateRangePrompt } from "../SelectDateRangePrompt";
import { TileContentGreyBackground } from "../TileContentGreyBackground";
import { useWasteFacility } from "../useWasteFacility";
import { useWasteFacilityState } from "../WasteFacilityContext";
import { WasteGenerationByClassificationGraph } from "./WasteGenerationByClassificationGraph";
import { WasteGenerationGraph } from "./WasteGenerationGraph";

const WASTE_GENERATION_CHARTS = gql(`
  query WasteGenerationCharts($facilityId: ID!, $filter: FacilityWasteGenerationFilter!) {
    facilityWasteGenerationCharts(facilityId: $facilityId, filter: $filter) {
      totalFederalHazardousLbs
      totalStateOnlyLbs
      totalAcuteHazardousLbs
      highestCalculatedGeneratorStatus
      generationByMonth {
        month
        federalHazardousLbs
        stateOnlyLbs
        acuteHazardousLbs
        calculatedFederalGeneratorStatus
        reportedFederalGeneratorStatus
        shipmentCalculatedFederalGeneratorStatus
        divergesFromShipmentCalculatedStatus
        byClassification {
          classification
          lbs
        }
      }
    }
  }
`);

type View = "generatorStatus" | "byClassification";

const VIEW_LABELS: Record<View, string> = {
  generatorStatus: "Generator status",
  byClassification: "By waste classification",
};

export const WasteGeneratedTile = () => {
  return (
    <DashboardTile size={12}>
      <WasteGeneratedContent />
    </DashboardTile>
  );
};

function WasteGeneratedContent() {
  const theme = useTheme();
  const [view, setView] = useState<View>("generatorStatus");
  const {
    wasteFacilityState: { deferredFilters, hasDateRange },
    effectiveWasteFacilityId,
  } = useWasteFacilityState();
  // Source the facility (and its state, used for threshold selection) from the
  // SAME facility id the chart query uses. Otherwise, when the user toggles to
  // a colocated waste facility in a different state, the chart data would be
  // compared against the original facility's state thresholds.
  const { data: facilityData } = useWasteFacility(effectiveWasteFacilityId);

  const skip = useMemo(
    () => !deferredFilters.startDate || !deferredFilters.endDate,
    [deferredFilters]
  );

  const variables = useMemo(
    () => ({
      facilityId: effectiveWasteFacilityId,
      filter: {
        startDate: deferredFilters.startDate,
        endDate: deferredFilters.endDate,
      },
    }),
    [deferredFilters.startDate, deferredFilters.endDate, effectiveWasteFacilityId]
  );

  const { data } = useSuspenseQuery(WASTE_GENERATION_CHARTS, skip ? skipToken : { variables });

  const charts = data?.facilityWasteGenerationCharts;
  const hasData = (charts?.generationByMonth.length ?? 0) > 0;

  return (
    <>
      <DashboardTile.Header
        title={
          <Stack direction="row" alignItems="center" gap={PREVIEW_ICON_GAP}>
            <span>Waste Generated</span>
            <StaffInDevelopmentIcon flag={FeatureFlagKey.WasteGenerationCard} />
          </Stack>
        }
      >
        <Stack direction="row" spacing={2}>
          <FormControl sx={{ width: theme.spacing(32.5) }}>
            <InputLabel id="waste-generated-view-by">View by</InputLabel>
            <Select
              sx={{ height: theme.spacing(5) }}
              fullWidth
              labelId="waste-generated-view-by"
              label="View by"
              value={view}
              onChange={(event) => setView(event.target.value as View)}
            >
              {(Object.entries(VIEW_LABELS) as [View, string][]).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DashboardTile.Header>

      <DashboardTile.ContentArea sx={{ flex: 1 }}>
        {!hasDateRange ? (
          <SelectDateRangePrompt />
        ) : !data ? (
          <Stack gap={2}>
            <Skeleton variant="rounded" height={40} />
            <Skeleton variant="rounded" height={350} />
          </Stack>
        ) : hasData && charts ? (
          view === "generatorStatus" ? (
            <WasteGenerationGraph
              data={charts.generationByMonth}
              totalFederalHazardousLbs={charts.totalFederalHazardousLbs}
              totalStateOnlyLbs={charts.totalStateOnlyLbs}
              totalAcuteHazardousLbs={charts.totalAcuteHazardousLbs}
              highestCalculatedGeneratorStatus={charts.highestCalculatedGeneratorStatus ?? null}
              facilityState={facilityData?.facility?.state}
            />
          ) : (
            <WasteGenerationByClassificationGraph data={charts.generationByMonth} />
          )
        ) : (
          <TileContentGreyBackground>🗑️ No waste generation data to show</TileContentGreyBackground>
        )}
      </DashboardTile.ContentArea>
    </>
  );
}
